import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Card, ErrorState, Spinner, cx } from '@/components/ui';
import { formatDateTime, formatKrw } from '@/lib/format';
import { AI_REVIEW_MARK, wantsReviewScroll } from './aiReviewMark';
import {
  HitProbability,
  ProbabilityDisclaimer,
  QuoteCell,
  ResultBadge,
  ScoreBar,
  StockIdentity,
  TargetRange,
} from './components';
import { resolveQuote } from './priceDisplay';
import { StockDetailModal } from './StockDetailModal';
import { StockSearchBox } from './StockSearchBox';
import { WatchlistPanel } from './WatchlistPanel';
import { PERIOD_OPTIONS, type Recommendation, type TradingProfile } from './types';
import type { Price } from './stockTypes';
import { usePrices } from './useStocks';
import { ProfileBadge, ProfileTabs } from './ProfileTabs';
import { TradingNav } from './TradingNav';
import { useMarketScope } from './marketScope';
import { useRecommendations } from './useTrading';
import { useRestingProfiles } from './usePaper';
import { useUsdKrwRate } from './useStocks';
import { AiReviewPanel } from './AiReviewPanel';

/**
 * 추천 목록.
 *
 * 🔴 이 화면에서 가장 자주 보게 될 것은 "빈 화면" 이다.
 *   실측상 절반이 넘는 날이 추천 0건이다. 상승 국면에서는 이격도 조건에서
 *   전부 탈락한다. (docs/impl/17-first-run.md, 20-indicator-distribution.md)
 *
 *   그래서 0건을 "데이터 없음" 으로 처리하면 매일 고장난 것처럼 보인다.
 *   기준을 통과한 종목이 없다는 것 자체가 결과이고, 기준을 낮춰 억지 후보를
 *   만들지 않는다는 것을 화면에서도 말해준다. (CLAUDE.md 1.3절)
 */
/** 한 번에 펼치는 추천 카드 수. 카드 하나가 점수·목표가·분석까지 담아 길다. */
const REVEAL_STEP = 10;

export function RecommendationListPage() {
  const [days, setDays] = useState<number>(PERIOD_OPTIONS[0].days);

  // undefined 가 전체 탭이다. API 파라미터를 생략하는 것과 뜻이 같다. (17단계)
  const [profile, setProfile] = useState<TradingProfile | undefined>(undefined);
  const resting = useRestingProfiles();

  /*
    국내/해외 탭 (2026-08-20, 미국 지원 Phase A).

    검색 범위·관심종목 표시·통화가 이 하나를 따라간다.
    해외 탭에는 추천이 없다 - 미국은 선별 대상이 아니다 (임계값을 미국 분포로
    재기 전까지. Phase B). 검색·관심종목·시세·차트·저점매수 알림까지만 제공한다.
  */
  // 🔴 2026-09-19 로컬 state 에서 공유 스토어로 옮겼다 (I-53)
  //   주도주·섹터·모의투자도 같은 선택을 따라야 한다. 화면마다 따로 기억하면
  //   미국을 보다가 다른 탭으로 넘어갔을 때 조용히 국내로 돌아온다.
  const country = useMarketScope((s) => s.scope);

  /*
    국내 하위 탭 — 자동 선별 결과 | 저점 신호 검토 (2026-10-17 · BACKLOG I-94 · docs/impl/153 23.4)
    주소의 ?view=review 로 가른다(시장지표 ?tab= 와 같은 방식) — 뒤로 가기 · 새로 고침 · 링크 공유에도 같은 탭이다.
    값이 없거나 모르는 값이면 지금까지처럼 자동 선별 결과다. 라우트(App.tsx · 공유 영역)는 건드리지 않는다.
  */
  const [params, setParams] = useSearchParams();
  const view: RecoView = params.get('view') === 'review' ? 'review' : 'auto';

  // 스윙(6개월) 안내의 [보고서 보기]로 왔으면 하위 탭 자리로 내려 보인다 — 위의 검색 · 관심종목에 가려 폰에선 안 보였다(2026-10-19 사용자)
  //   하위 탭을 눌러 바꿀 때는 state 가 없어 움직이지 않는다 · scrollIntoView 가 없는 곳(jsdom)에서도 깨지지 않게 있을 때만 부른다
  const location = useLocation();
  const viewTabsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (view === 'review' && wantsReviewScroll(location.state)) {
      viewTabsRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  }, [view, location.state]);

  const query = useRecommendations(days, profile);

  // 해외 탭에서만 환율을 읽는다. 미국 가격의 원화 감을 잡는 참고선이다 (2026-08-20)
  const exchangeRate = useUsdKrwRate(country === 'US');

  /*
    카드 점진 표시 (2026-08-20 사용자 요청 - 이력이 세로로 한없이 길어진다).

    표가 아니라 카드 피드라 이전/다음 페이지네이션 대신 "더 보기" 를 쓴다.
    페이지를 넘기면 스크롤이 튀는데, 카드는 위의 것과 비교하며 읽는 물건이다.

    기간·프로필을 바꾸면 처음 분량으로 되돌린다. 90일 탭에서 다 펼쳐둔
    상태가 7일 탭으로 넘어와 남아 있을 이유가 없다.
  */
  const [visibleCount, setVisibleCount] = useState(REVEAL_STEP);
  useEffect(() => {
    setVisibleCount(REVEAL_STEP);
  }, [days, profile]);

  /*
    보이는 카드의 현재가·등락률 (2026-08-28 사용자 요청).
    usePrices 가 15초 폴링을 이미 하고 있어 여기서는 종목 id 만 모은다.
    보이는 만큼만 물어본다 - 90일 이력 전체의 시세는 아무도 안 본다.
  */
  const visibleStockIds = (query.data ?? [])
    .slice(0, visibleCount)
    .map((item) => item.stockId);
  const prices = usePrices([...new Set(visibleStockIds)]);

  // 모달로 열어 둔 종목. null 이면 닫혀 있다.
  // 라우트를 따로 두지 않는 이유는 모달을 닫으면 원래 자리로 돌아와야 하고,
  // 추천 목록의 스크롤 위치를 잃지 않아야 하기 때문이다.
  const [openStockId, setOpenStockId] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <TradingNav />


      {/*
        🔴 이 화면만 "준비 중" 안내를 위에 얹지 않는다 (2026-09-20)
          검색 · 관심종목은 두 시장이 함께 쓰는 공통 영역이라 미국에서도 그대로 동작한다.
          여기서 준비되지 않은 것은 자동 종목 선별 결과 하나뿐이고, 그것은 아래에서
          통째로 숨기며 이유를 따로 적는다. 위에 "준비 중" 을 또 얹으면 쓸 수 있는
          영역까지 못 쓰는 것처럼 읽힌다. 다른 네 화면은 화면 전체가 국내 전용이라
          early return 한다.
      */}

      {/*
        검색을 별도 페이지로 두지 않는다. 검색은 목적지가 아니라 행동이다.
        (docs/01-trading.md 8.1절)
      */}
      <StockSearchBox country={country} onSelect={(stock) => setOpenStockId(stock.id)} />

      <WatchlistPanel country={country} onSelect={setOpenStockId} />

      <StockDetailModal stockId={openStockId} onClose={() => setOpenStockId(null)} />

      {country === 'US' && (
        <div className="rounded-lg bg-surface-muted px-3 py-2 text-xs text-fg-muted">
          {/*
            🔴 시각을 문구에 박지 않는다 (2026-09-20)

            전에는 "미국 정규장(한국시간 밤 10시 30분 ~ 새벽 5시)" 이라고 적었는데 두 군데가 틀렸다.
              ① 서머타임 — 미국 표준시(11월 초~3월 중순)에는 23:30~06:00 이다. 연 4개월 틀린 문장이었다
              ② 범위 — RealtimePriceJob 은 isTradingHours("US") 로 판정하는데 그 메서드는
                 정규장뿐 아니라 프리마켓·애프터마켓까지 포함한다(MarketCalendarService).
                 즉 실제 갱신 구간이 문구보다 넓었다

            배치 자신도 시각을 cron 에 박지 않고 캘린더에 묻는다 - 같은 이유다(RealtimePriceJob 주석).
            화면도 같은 태도를 취한다. 시각을 적어야 할 일이 생기면 API 로 받아 그린다.
          */}
          <p>
            {/*
              ✏️ 2026-09-28 I-78 2차 — "추천과 모의투자 계좌는 준비 중" 이었다. 미국 저점매수 모의 계좌(US_P20)는
                열렸다(V141 · AI 모의투자 탭) — 4회차 전이라 매수만 꺼져 있다. 시간대도 종목 모달과 맞췄다
                (프리마켓이 한국시간 17:00 부터라 "밤" 은 좁다 — StockDetailModal 주석 9/20)
            */}
            미국 종목은 이 화면의 선별 결과에는 나오지 않습니다. 4회차부터 매일 오전 10:45 에 고르는
            미국 추천은 [AI 모의투자]의 🇺🇸 미국 추천 탭에 있습니다. 검색·관심종목·시세·차트·가격
            알림을 제공하며, 시세는 <strong>미국 시장이 열려 있는 동안</strong>(한국시간
            오후~다음날 아침, 프리마켓·애프터마켓 포함) 갱신됩니다. 미국 계좌(미국 저점매수 · 미국 스윙 ·
            미국 추천)는 AI 모의투자 탭에 있고, 4회차부터 매수를 시작합니다.
          </p>
          {/* 참고용 표시 환율. 매시 정각 저장이라 기준 시각을 밝힌다 (2026-08-20) */}
          {exchangeRate.data && (
            <p className="mt-1">
              참고 환율 $1 = {formatKrw(exchangeRate.data.rate)} ·{' '}
              {formatDateTime(exchangeRate.data.fetchedAt)} 기준
            </p>
          )}
        </div>
      )}

      {country === 'US' ? null : (
        <>
          {/*
        화면 사이 이동은 위의 TradingNav 가 맡는다. (2026-08-19)
        전에는 여기 제목 옆에 인라인 링크를 뒀는데, 모의투자까지 셋이 되면서
        제목 줄이 링크로 붐볐다. 탭으로 올렸다.
      */}
          {/* 하위 탭 — 제목 자리("자동 종목 선별 결과")를 탭 둘로 바꿨다(2026-10-17 · I-94) */}
          <div ref={viewTabsRef} className="scroll-mt-4">
            <RecoViewTabs
              view={view}
              onChange={(next) => setParams(next === 'review' ? { view: 'review' } : {}, { replace: true })}
            />
          </div>

          {view === 'review' ? (
            <AiReviewPanel />
          ) : (
            <>
      <PeriodTabs days={days} onChange={setDays} />

      {/* 프로필 탭. 두 기준은 완화 관계가 아니라 서로 다른 기준이다 (17단계) */}
      {/* 쉬는 추천 프로필 탭은 숨긴다 — 4회차부터 분할익절 · 하이리스크는 새 추천이 없다(2026-10-10 · 141번 10.6) */}
      <ProfileTabs value={profile} onChange={setProfile} hidden={resting} />

      {/*
        🔴 저점매수가 왜 여기 없는지 적는다 (2026-08-31 사용자 질문)

          모의투자 계좌는 여섯인데 이 화면의 탭은 셋이라 "빠진 것" 으로 읽힌다.
          실제로는 저점매수 계열이 스크리너를 쓰지 않아 나올 자리가 없다 -
          그쪽은 왕복성 상위 100종목을 한 달간 고정해 두고 그 안에서만 산다.
          화면이 그 사실을 말하지 않으면 매번 같은 의문이 생긴다.

          🔴 2026-09-20 에 이 주석이 "다섯" · "50종목" 으로 낡아 있었다.
            바로 아래 화면 문구는 맞게 고쳐졌는데 주석만 따라가지 않았다.
            다음에 이 문단을 손보는 사람이 주석을 근거로 화면을 되돌릴 뻔했다.
      */}
      <p className="mt-2 rounded-lg border border-line bg-surface-muted p-2 text-xs text-fg-subtle">
        <strong>저점매수 계열(3개)과 스윙 계좌(2개)는 여기에 없습니다.</strong> 이 탭은 평일 21:05에
        코스피 전체를 훑어 점수를 매기는 <strong>자동 선별</strong>의 결과이고, 추천 계좌(안전 · 분할익절 ·
        하이리스크)가 그 결과를 삽니다(4회차부터는 <strong>안전</strong> 계좌만 새로 사고, 분할익절 · 하이리스크는
        들고 있는 종목만 정리합니다). 스윙(6개월) · 스윙(3개월)도 선별을 쓰지 않고 [AI 모의투자]의 스윙
        탭에 있는 자기 감시 대상에서 삽니다(4회차부터). 저점매수 · 저점매수(리스크) · 저점매수(챌린저)는 선별을 쓰지 않고
        <strong> 왕복이 잦은 100종목을 한 달간 고정</strong>해 두고 그 안에서만 사고팝니다 —
        그쪽 목록은 [AI 모의투자]의 해당 탭에 있는 <strong>감시 대상</strong> 표입니다.
        다만 <strong>챌린저</strong>는 100종목을 보되 <strong>새로 사는 것은 상위 30종목까지</strong>입니다.
        스윙(6개월) 감시 종목이 1단선에 닿은 기록(매매와 무관한 참고 자료)은 옆 [{AI_REVIEW_MARK} 저점 신호 검토] 탭에 있습니다.
      </p>

      {query.isLoading && <Spinner />}

      {query.error && (
        <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
      )}

      {query.data && query.data.length === 0 && (
        <NoCandidates days={days} onWiden={setDays} />
      )}

      {query.data && query.data.length > 0 && (
        <>
          {query.data.slice(0, visibleCount).map((item) => (
            <RecommendationCard
              key={item.id}
              item={item}
              price={prices.data?.find((p) => p.stockId === item.stockId) ?? null}
            />
          ))}

          {query.data.length > visibleCount && (
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setVisibleCount((count) => count + REVEAL_STEP)}
            >
              더 보기 ({query.data.length - visibleCount}건 남음)
            </Button>
          )}

          <ProbabilityDisclaimer />
        </>
      )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 국내 하위 탭 (2026-10-17 · I-94)
// ─────────────────────────────────────────────

type RecoView = 'auto' | 'review';

const RECO_VIEWS: { key: RecoView; label: string }[] = [
  { key: 'auto', label: '자동 선별 결과' },
  // 🔎 = AI 2차 검토 표식(스윙(6개월) 탭 · 감시 표 칩과 같은 글자 · 2026-10-18)
  { key: 'review', label: `${AI_REVIEW_MARK} 저점 신호 검토` },
];

/** 자동 선별 결과 | 저점 신호 검토 — 시장지표 하위 탭과 같은 모양(ExchangeRatePage) */
function RecoViewTabs({ view, onChange }: { view: RecoView; onChange: (view: RecoView) => void }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1 border-b border-line pb-2" role="tablist" aria-label="추천 하위 탭">
      {RECO_VIEWS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={view === t.key}
          onClick={() => onChange(t.key)}
          className={cx(
            'min-h-[36px] rounded-lg px-3 py-1.5 text-sm font-medium',
            view === t.key ? 'bg-brand text-brand-fg' : 'text-fg-muted hover:bg-surface-hover',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// 기간 선택
// ─────────────────────────────────────────────

function PeriodTabs({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  return (
    <div className="flex gap-1">
      {PERIOD_OPTIONS.map((option) => (
        <button
          key={option.days}
          type="button"
          onClick={() => onChange(option.days)}
          className={cx(
            'min-h-[36px] rounded-lg px-3 text-sm',
            days === option.days
              ? 'bg-brand text-brand-fg'
              : 'text-fg-muted hover:bg-surface-hover',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// 0건
// ─────────────────────────────────────────────

/**
 * 추천 후보가 없을 때.
 *
 * "없음" 만 띄우지 않는 이유
 *   사용자가 매일 보는 화면이고, 그중 절반이 이 상태다. 아무 설명이 없으면
 *   배치가 죽은 것인지 정말 없는 것인지 구분할 수 없다.
 *
 * 기간을 넓히는 버튼을 두는 이유
 *   7일이 0건이어도 30일에는 있을 수 있다. 마지막 추천이 언제였는지를
 *   확인하는 것만으로도 "파이프라인은 살아 있다" 를 알 수 있다.
 */
function NoCandidates({ days, onWiden }: { days: number; onWiden: (days: number) => void }) {
  const 다음기간 = PERIOD_OPTIONS.find((option) => option.days > days);

  return (
    <Card>
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm font-medium text-fg">
          최근 {days}일간 조건을 통과한 종목이 없습니다.
        </p>

        <p className="max-w-sm text-xs leading-relaxed text-fg-muted">
          오류가 아닙니다. 선별 기준을 통과한 종목이 실제로 없다는 뜻이고,
          기준을 낮춰 억지로 후보를 만들지 않습니다.
          <br />
          상승 국면에서는 이격도 조건에서 대부분 탈락합니다.
        </p>

        {다음기간 && (
          <Button variant="secondary" onClick={() => onWiden(다음기간.days)}>
            {다음기간.label}로 넓혀 보기
          </Button>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// 추천 카드
// ─────────────────────────────────────────────

/**
 * 추천 한 건.
 *
 * 표가 아니라 카드인 이유는 폰에서 보기 때문이다.
 * 종목명·점수·가격·도달률까지 한 줄에 넣으면 가로로 한참 흐른다.
 *
 * 카드 전체가 상세로 가는 링크다. 폰에서 작은 "자세히" 글자를 누르게 하지 않는다.
 */
function RecommendationCard({
  item,
  price,
}: {
  item: Recommendation;
  price: Price | null;
}) {
  return (
    <Link to={`/trading/${item.id}`} className="block">
      <Card className="transition-colors hover:border-line-strong">
        <header className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-fg">{item.name}</p>
            {/* 거래소 + 종목코드. 시세 응답이 거래소를 주고, 없으면 코드만 찍는다 */}
            <StockIdentity market={price?.market ?? null} symbol={item.symbol} />
            <p className="text-xs text-fg-subtle">{formatDateTime(item.recommendedAt)}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {/*
              현재가 + 등락률 (2026-08-28). 증권사 앱처럼 행 안에서 바로 본다.
              추천 시점의 진입가와 지금 가격이 나란히 보여야
              "지금 사도 되는 가격인가" 를 카드에서 판단할 수 있다.
            */}
            {price && (
              <span className="flex items-baseline gap-1">
                <QuoteCell view={resolveQuote(price, null, null)} />
              </span>
            )}
            <div className="flex items-center gap-1">
            {/* 전체 탭에서는 두 기준이 섞여 보인다. 카드마다 어느 쪽인지 적는다 */}
            <ProfileBadge profile={item.profile} label={item.profileLabel} />
            <ResultBadge result={item.result} />
            </div>
          </div>
        </header>

        <div className="mt-3">
          <ScoreBar score={item.score} />
        </div>

        <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <div>
            <dt className="text-fg-subtle">진입</dt>
            <dd className="tabular text-fg">{formatKrw(item.entryPrice)}</dd>
          </div>
          <div>
            <dt className="text-fg-subtle">목표</dt>
            <dd className="tabular text-rise">{formatKrw(item.targetPrice)}</dd>
          </div>
          <div>
            <dt className="text-fg-subtle">손절</dt>
            <dd className="tabular text-fall">{formatKrw(item.stopLossPrice)}</dd>
          </div>
        </dl>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <TargetRange
            targetPercent={item.targetPercent}
            stopLossPercent={item.stopLossPercent}
          />
          <HitProbability probability={item.hitProbability} samples={item.sampleSize} />
        </div>
      </Card>
    </Link>
  );
}
